/**
 * Route table (Block 6.A / ADR-024 D2 + D7-bis; Block 6.C.1 adds /lookups;
 * Block 6.F.5 consolidates /lookups + /suggestions + /bulk-add into
 * /admin-review with 302 redirects from the old paths; Phase X.5.1 makes
 * /hub the landing surface).
 *
 * Routes:
 *   /                                     → redirect to /t/{defaultTenant}/hub (X.5.1)
 *   /t/:tenantSlug/hub                    → Hub (the Olive home page; landing surface)
 *   /t/:tenantSlug/directory              → Directory (surface 1; still reachable)
 *   /t/:tenantSlug/organizations/:id      → OrganizationDetail (surface 2)
 *   /t/:tenantSlug/individuals/:id        → IndividualDetail (surface 3)
 *   /t/:tenantSlug/admin-review           → AdminReview (Block 6.F.5; consolidated steward surface)
 *   /t/:tenantSlug/my-suggestions         → MySuggestions (per-user; any role)
 *   /t/:tenantSlug/lookups                → 302 redirect to admin-review?tab=lookups (Block 6.F.5)
 *   /t/:tenantSlug/suggestions            → 302 redirect to admin-review?tab=pending (Block 6.F.5)
 *   /t/:tenantSlug/bulk-add               → 302 redirect to admin-review?tab=bulk (Block 6.F.5)
 *   *                                     → catch-all 404
 *
 * `defaultTenant` from @/lib/auth is consumed exactly here for the root
 * redirect — the second-and-only-other site in the codebase per D7-bis
 * one-site discipline. Era B replaces the literal redirect with a
 * whoami-derived target.
 *
 * Block 6.F.5 [r2 fix L1]: old-route 302 redirects stay indefinitely.
 * Bookmarks have long tails; the cost of a navigate-shim is negligible
 * vs. operator friction of broken bookmarks. Old prescription "remove
 * during 6.F.7 cleanup" was a sub-block reference mistake (6.F.7 is a
 * peace-hub repo edit, not peace-os cleanup).
 */

import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { type ReactNode } from 'react';
import { defaultTenant, useTenant } from '@/lib/auth';
import { canViewAnyClientRoster } from '@/lib/capabilities';
import { captureQboConnectToken } from '@/lib/qboConnectSignal';
import { AppShell } from '@/components/AppShell';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { NotFoundPage } from '@/components/NotFoundPage';
import { DirectoryHub } from '@/pages/DirectoryHub';
import { OrganizationDetail } from '@/pages/OrganizationDetail';
import { IndividualDetail } from '@/pages/IndividualDetail';
import { AdminReview } from '@/pages/AdminReview';
import { MySuggestions } from '@/pages/MySuggestions';
import { NotificationsInbox } from '@/pages/NotificationsInbox';
import { NotificationSettings } from '@/pages/NotificationSettings';
import { MyTime } from '@/pages/MyTime';
import { BakeOffLeague } from '@/pages/BakeOffLeague';
import { PayrollHours } from '@/pages/PayrollHours';
import { MergeReview } from '@/pages/MergeReview';
import { AiTools } from '@/pages/AiTools';
import { EmailDrafter } from '@/pages/EmailDrafter';
import { AssessmentDrafter } from '@/pages/AssessmentDrafter';
import { AssessmentLetter } from '@/pages/AssessmentLetter';
import { BrandFormatter } from '@/pages/BrandFormatter';
import { MedicationList } from '@/pages/MedicationList';
import { IntakeSummary } from '@/pages/IntakeSummary';
import { AiToolsDrafts } from '@/pages/AiToolsDrafts';
import { AiToolsDraftDetail } from '@/pages/AiToolsDraftDetail';
import { Hub } from '@/pages/Hub';
import { Team } from '@/pages/Team';
import { Invoices } from '@/pages/Invoices';
import { InvoicePreview } from '@/pages/InvoicePreview';
import { ClientBilling } from '@/pages/ClientBilling';
import { PrebillingAll } from '@/pages/PrebillingAll';
import { Workspace } from '@/pages/Workspace';
import { Clients } from '@/pages/Clients';
import { ClientRecord } from '@/pages/ClientRecord';
import { CareManagerView } from '@/pages/CareManagerView';
import { ClientsHome } from '@/pages/ClientsHome';
import { Reports } from '@/pages/Reports';
import { OperatingReports } from '@/pages/OperatingReports';

/**
 * Block 6.F.5 redirect shim: hop the user to /admin-review with the
 * appropriate ?tab= query param, preserving any other query params from
 * the old URL (e.g. /lookups?type=specialties&include=deprecated →
 * /admin-review?tab=lookups&type=specialties&include=deprecated).
 */
function AdminReviewRedirect({ tab }: { tab: 'pending' | 'bulk' | 'lookups' }): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const sp = new URLSearchParams(searchParams);
  sp.set('tab', tab);
  return <Navigate to={`/t/${tenantSlug ?? defaultTenant}/admin-review?${sp.toString()}`} replace />;
}

/**
 * PR-C1 redirect shim: the legacy standalone notes surface
 * (/clients/:clientId/notes) is now the "Notes & activity" tab of the
 * client record page. Hop to /clients/:clientId?tab=notes, preserving any
 * other query params (e.g. the old ?name=). Declared in REDIRECT_ROUTES.
 */
function ClientNotesRedirect(): ReactNode {
  const { tenantSlug, clientId } = useParams<{ tenantSlug: string; clientId: string }>();
  const [searchParams] = useSearchParams();
  const sp = new URLSearchParams(searchParams);
  sp.set('tab', 'notes');
  return (
    <Navigate
      to={`/t/${tenantSlug ?? defaultTenant}/clients/${clientId ?? ''}?${sp.toString()}`}
      replace
    />
  );
}

/**
 * R9 redirect shim: /t/:tenantSlug/home (the retired "Your day" address)
 * hops to the Clients landing, which now renders that page.
 */
function ClientsLandingRedirect(): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return <Navigate to={`/t/${tenantSlug ?? defaultTenant}/clients`} replace />;
}

/**
 * Nav-rework redirect shim (2026-07-13): the retired X.1 Dashboard route.
 * The CareTree-fed Dashboard was consolidated into the native operations
 * Dashboard at /reports/operating (frozen imports made its Revenue/Cash&AR
 * cards stale by construction). Old /dashboard bookmarks hop there,
 * preserving any query string + hash (the AdminReviewRedirect /
 * ClientNotesRedirect param-preserving precedent). Declared in
 * REDIRECT_ROUTES.
 */
function DashboardRedirect(): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { search, hash } = useLocation();
  return (
    <Navigate
      to={`/t/${tenantSlug ?? defaultTenant}/reports/operating${search}${hash}`}
      replace
    />
  );
}

/**
 * The capacity board and map moved off the Workspace onto the Team page's
 * tabs. The old address hops to /team?tab=capacity, keeping any other
 * query params (the ?week= pick). Declared in REDIRECT_ROUTES.
 */
function CapacityRedirect(): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const sp = new URLSearchParams(searchParams);
  sp.set('tab', 'capacity');
  return <Navigate to={`/t/${tenantSlug ?? defaultTenant}/team?${sp.toString()}`} replace />;
}

/**
 * F11: the QBO OAuth callback lands on '/?qbo_connect=<token>' and the
 * root <Navigate replace> strips the query — so before this component
 * existed, every callback outcome (a declined consent included) vanished
 * on the way to the hub. Capture the token FIRST, then redirect exactly
 * as before. The capture is synchronous and idempotent on purpose:
 * <Navigate> fires its navigation from a child effect, which runs BEFORE
 * a parent effect would, so a useEffect here could lose the race.
 * QboConnectResultBanner (billing surface) is the reader.
 */
function RootRedirect(): ReactNode {
  const { search } = useLocation();
  captureQboConnectToken(search);
  // X.5.1: root → /t/peace/hub (the Olive home page is the landing surface).
  // D7-bis bootstrap-shim: fires before whoami; whoami reconciliation in
  // TenantContext replaces the slug if the resolved tenant differs (Era B).
  return <Navigate to={`/t/${defaultTenant}/hub`} replace />;
}

/**
 * /clients landing gate: a viewer who may open the roster
 * (canViewAnyClientRoster: view_all_clinical, or the engagement lane
 * with a roster flag) and has no caseload (whoami.has_caseload === false
 * — no active client names their roster row as primary care manager or
 * SPICE therapist, and they cover no SPICE visit plan) has no day to
 * open, so /clients opens the full roster instead of an empty "Your
 * day". A viewer who does have a caseload keeps the day page regardless
 * of role or the does_care_management / does_spice flags — those
 * describe what a person is allowed to log, not whether they have
 * clients. A viewer who cannot open the roster always opens the day
 * page. The predicate reads whoami only — no request beyond the one
 * ClientsHome already makes when it renders. Renders nothing until
 * whoami has loaded, so a viewer never sees one page flash before the
 * other.
 */
export function ClientsLandingGate(): ReactNode {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const [searchParams] = useSearchParams();
  if (!whoami) return null;
  // A task link (?item=: My Tasks and the task notification) opens the
  // task's drawer, which lives on the day page; sending that viewer to the
  // roster would drop the task they came to open.
  const opensOnEmptyDay =
    canViewAnyClientRoster(whoami) && whoami.has_caseload === false && !searchParams.has('item');
  if (opensOnEmptyDay) {
    return <Navigate to={`/t/${tenantSlug ?? defaultTenant}/clients/all`} replace />;
  }
  return <ClientsHome />;
}

/**
 * Route table. Exported as a plain array so tests can inspect the root
 * redirect target + mount individual routes via createMemoryRouter without
 * a real browser history. `router` (the createBrowserRouter instance) is
 * what App.tsx consumes.
 */
export const routes = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    // Phase X.5.1 — Olive hub: the in-app home/landing surface.
    // Renders the logged-in user's OWN cap-gated tiles + announcements +
    // daily quote. No tier switcher in production.
    path: '/t/:tenantSlug/hub',
    element: (
      <AppShell>
        <Hub />
      </AppShell>
    ),
  },
  {
    // The Team page: the directory (every internal seat's read-only
    // who-is-who, with birthdays and anniversaries), the capacity board and
    // the map as ?tab= views. Registry entry 'team' in navSection.ts; the
    // page gates on canViewStaffTools and the board tabs on canViewWorkspace.
    path: '/t/:tenantSlug/team',
    element: (
      <AppShell>
        <Team />
      </AppShell>
    ),
  },
  {
    // R9 (founder live-walk 2026-07-06): the old "Your day" address.
    // The task-first page IS the Clients landing now; this redirect keeps
    // bookmarks from the soak alive. Declared in REDIRECT_ROUTES.
    path: '/t/:tenantSlug/home',
    element: <ClientsLandingRedirect />,
  },
  {
    // Nav rework 2026-07-13: the retired X.1 Dashboard route now 302-redirects
    // to the native operations Dashboard (/reports/operating). Declared in
    // REDIRECT_ROUTES; the page + its dashboard/* FE were deleted.
    path: '/t/:tenantSlug/dashboard',
    element: <DashboardRedirect />,
  },
  {
    // Reports Phase A — referral-partner analytics. Page gates on
    // canViewReports (owner-only); registry entry 'reports' in navSection.ts.
    path: '/t/:tenantSlug/reports/referrals',
    element: (
      <AppShell>
        <Reports />
      </AppShell>
    ),
  },
  {
    // Billing slice A4 — invoice list + compile entry. view_invoice gates the
    // page; canCompileInvoice (owner-only) gates the compile form.
    path: '/t/:tenantSlug/invoices',
    element: (
      <AppShell>
        <Invoices />
      </AppShell>
    ),
  },
  {
    // The operations Dashboard (née Reports Phase B operating metrics; the
    // nav rework 2026-07-13 renamed the entry after consolidating the retired
    // X.1 Dashboard into this page). Page gates on canViewReports (owner +
    // board_observer, the read). The page has no upload door (live weeks
    // come from Olive billing). Registry entry 'dashboard' in navSection.ts.
    path: '/t/:tenantSlug/reports/operating',
    element: (
      <AppShell>
        <OperatingReports />
      </AppShell>
    ),
  },
  {
    // A4 — functional operator preview of one invoice (read + approve/issue/
    // reopen/discard, capability-gated). createBrowserRouter ranks by
    // specificity, so this does not shadow the /invoices list route above.
    path: '/t/:tenantSlug/invoices/:id',
    element: (
      <AppShell>
        <InvoicePreview />
      </AppShell>
    ),
  },
  {
    // The master Pre-billing page: every client's note lines for a date
    // range (view_master_prebilling). More specific than /invoices/:id, so
    // it ranks first.
    path: '/t/:tenantSlug/invoices/prebilling',
    element: (
      <AppShell>
        <PrebillingAll />
      </AppShell>
    ),
  },
  {
    // One client's billing settings without the client record: the rates,
    // the bill-to and household, the QuickBooks link. view_invoice gates the
    // page. More specific than /invoices/:id, so it ranks first.
    path: '/t/:tenantSlug/invoices/clients/:clientId',
    element: (
      <AppShell>
        <ClientBilling />
      </AppShell>
    ),
  },
  {
    // PR4a — the Workspace board (Intake Pipeline / My Work / Tasks). The page
    // gates on canViewWorkspace; ?view= selects the tab, ?item= deep-links a
    // card drawer (the bell click-through target).
    path: '/t/:tenantSlug/workspace',
    element: (
      <AppShell>
        <Workspace />
      </AppShell>
    ),
  },
  {
    // The capacity board's old address: it lives on the Team page now.
    // Redirects to /team?tab=capacity; declared in REDIRECT_ROUTES.
    path: '/t/:tenantSlug/workspace/capacity',
    element: <CapacityRedirect />,
  },
  {
    // R9 — the CLIENTS LANDING: the Clients nav pill lands on the
    // task-first page (needs-attention / your-clients / on-your-clients),
    // except for a view_all_clinical viewer with no caseload, who has
    // nothing to fill that page and opens the full roster instead
    // (ClientsLandingGate). Page gates
    // read_clinical_record (all six roles); the record links inside
    // respect the record page's own soak gate. Registry entry 'clients'
    // in navSection.ts.
    path: '/t/:tenantSlug/clients',
    element: (
      <AppShell>
        <ClientsLandingGate />
      </AppShell>
    ),
  },
  {
    // TABLE-STAKES TS-1 — the full Clients roster list (Include-archived +
    // Add-client), behind the landing's "All clients →" link (R9). The
    // page gates on canViewAnyClientRoster: the office width, or a line
    // clinician's engagement lane (ADR-0053), whose list the server
    // narrows to her lane. Declared in DETAIL_ROUTES under 'clients'.
    // createBrowserRouter ranks static segments above dynamic ones, so
    // /clients/all does not collide with the /clients/:clientId record
    // route below.
    path: '/t/:tenantSlug/clients/all',
    element: (
      <AppShell>
        <Clients />
      </AppShell>
    ),
  },
  {
    // Kith client record page (PR-C1) — the pattern-setter: vitals band +
    // ?tab= shell (Overview | Notes & activity | Care record | People |
    // Documents | Pre-billing). The page opens on read_clinical_record;
    // whether THIS client's record opens is the server's care-team gate
    // (assertViewerMaySeeClient: the office width, the engagement lane,
    // or membership). Declared in DETAIL_ROUTES under 'clients'.
    // createBrowserRouter ranks by specificity, so this does not shadow
    // the /clients list route above.
    path: '/t/:tenantSlug/clients/:clientId',
    element: (
      <AppShell>
        <ClientRecord />
      </AppShell>
    ),
  },
  {
    // Soak #2 top-bar #11 — the CARE-MANAGER VIEW: a CM + the list of their
    // clients, reached from the record header's "Care manager" link. Gated
    // view_all_clinical at the page for the soak (mirrors the Clients
    // roster); declared in DETAIL_ROUTES under 'clients'. createBrowserRouter
    // ranks static segments above dynamic ones, so this does not collide
    // with the /clients/:clientId record route.
    path: '/t/:tenantSlug/care-managers/:rosterId',
    element: (
      <AppShell>
        <CareManagerView />
      </AppShell>
    ),
  },
  {
    // Legacy standalone notes surface → now the record page's Notes tab.
    // 302-style client-side redirect to /clients/:clientId?tab=notes;
    // declared in REDIRECT_ROUTES. createBrowserRouter ranks by specificity,
    // so this does not shadow the /clients/:clientId detail route above.
    path: '/t/:tenantSlug/clients/:clientId/notes',
    element: <ClientNotesRedirect />,
  },
  {
    // Phase X.3.1 — CM AI Tools portal. Capability-gated landing +
    // per-tool detail shells + drafts inbox. Real input/output paths
    // land at X.3.4–X.3.6; drafts persistence lands at X.3.2.
    path: '/t/:tenantSlug/ai-tools',
    element: (
      <AppShell>
        <AiTools />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/email',
    element: (
      <AppShell>
        <EmailDrafter />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/assessment',
    element: (
      <AppShell>
        <AssessmentDrafter />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/assessment-letter',
    element: (
      <AppShell>
        <AssessmentLetter />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/brand-formatter',
    element: (
      <AppShell>
        <BrandFormatter />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/med-list',
    element: (
      <AppShell>
        <MedicationList />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/intake',
    element: (
      <AppShell>
        <IntakeSummary />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/ai-tools/drafts',
    element: (
      <AppShell>
        <AiToolsDrafts />
      </AppShell>
    ),
  },
  {
    // Phase X.3.4.7 — drafts-inbox click-through detail page (view +
    // edit body + finalize/archive). createBrowserRouter ranks by
    // specificity, so this does not shadow the /drafts list route above.
    path: '/t/:tenantSlug/ai-tools/drafts/:id',
    element: (
      <AppShell>
        <AiToolsDraftDetail />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/directory',
    element: (
      <AppShell>
        <DirectoryHub />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/organizations/:id',
    element: (
      <AppShell>
        <OrganizationDetail />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/individuals/:id',
    element: (
      <AppShell>
        <IndividualDetail />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/admin-review',
    element: (
      <AppShell>
        <AdminReview />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/my-suggestions',
    element: (
      <AppShell>
        <MySuggestions />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/notifications',
    element: (
      <AppShell>
        <NotificationsInbox />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/settings/notifications',
    element: (
      <AppShell>
        <NotificationSettings />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/bake-off',
    element: (
      <AppShell>
        <BakeOffLeague />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/my-time',
    element: (
      <AppShell>
        <MyTime />
      </AppShell>
    ),
  },
  {
    path: '/t/:tenantSlug/payroll',
    element: (
      <AppShell>
        <PayrollHours />
      </AppShell>
    ),
  },
  {
    // Phase 1.14.3 — merge-listings review + commit surface.
    // Entry points: inline "Mark as duplicate of…" on OrganizationDetail
    // + AdminReview Merges sub-tab + direct URL.
    path: '/t/:tenantSlug/merges/:eventId',
    element: (
      <AppShell>
        <MergeReview />
      </AppShell>
    ),
  },
  // Block 6.F.5 — old-route 302 redirects (stay indefinitely per r2 §3.3 [L1]).
  {
    path: '/t/:tenantSlug/lookups',
    element: <AdminReviewRedirect tab="lookups" />,
  },
  {
    path: '/t/:tenantSlug/suggestions',
    element: <AdminReviewRedirect tab="pending" />,
  },
  {
    path: '/t/:tenantSlug/bulk-add',
    element: <AdminReviewRedirect tab="bulk" />,
  },
  {
    path: '*',
    element: (
      <AppShell>
        <NotFoundPage />
      </AppShell>
    ),
  },
];

// A pathless root layout route renders <Outlet/> (pass-through) and supplies a
// site-wide errorElement, so a render/loader error from ANY child route shows
// the branded RouteErrorBoundary instead of React Router's default stack screen.
export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorBoundary />,
    children: routes,
  },
]);


